import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Switch,
  Button,
  Flex,
  Typography,
  message,
  Select,
  Spin,
} from "antd";
import {
  EnvironmentOutlined,
  CheckOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import {
  API_URL_QUOTES,
  API_URL_USER_ADDRESSES_V1,
  VITE_GOOGLE_MAPS_API_KEY,
} from "@/services/XCONS_ENV";
import {
  useJsApiLoader,
  Autocomplete,
  GoogleMap,
  MarkerF,
} from "@react-google-maps/api";
import xconsMap from "@/assets/img/xcons-map.svg";
import { PhoneNumberInput } from "@/components/phone-number-input";
import { AR_PHONE_INVALID_MESSAGE, isValidArPhone } from "../utils/arPhone";
import { isValidDni } from "../utils/quoteValidations";

const { Text } = Typography;

const API_KEY = VITE_GOOGLE_MAPS_API_KEY;
const libraries: "places"[] = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: 170,
  borderRadius: 8,
  border: "1px solid #f0f0f0",
};

const defaultCenter = { lat: -34.603722, lng: -58.381592 };

interface AddressAutocompleteInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onLoad: (autocomplete: google.maps.places.Autocomplete) => void;
  placeholder?: string;
}

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value,
  onChange,
  onLoad,
  placeholder,
}) => (
  <Autocomplete onLoad={onLoad}>
    <Input
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
    />
  </Autocomplete>
);

const ARGENTINA_PROVINCES = [
  "Buenos Aires",
  "CABA",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

interface DeliveryAddressDrawerProps {
  open: boolean;
  onClose: () => void;
  quoteId: number;
  shippingAddress: any;
  currentAddress: {
    city?: string;
    region?: string;
    latitude?: string;
    longitude?: string;
  };
  /** Owner userId; required to persist into the user's agenda. */
  userId?: number | null;
  /** Quote's storeCode; persisted alongside the agenda row so it shows up in the contact's address book filtered by store. */
  storeCode?: string | null;
  onSave: () => void;
  /** Contact data to pre-fill the form when the owner exists but has no saved address. */
  ownerContact?: { name?: string; phone?: string; vatId?: string };
  /** Opens the owner's address agenda. Shown as a button in the drawer title when present. */
  onOpenAgenda?: () => void;
}

export const DeliveryAddressModal: React.FC<DeliveryAddressDrawerProps> = ({
  open,
  onClose,
  quoteId,
  shippingAddress,
  currentAddress,
  userId,
  storeCode,
  onSave,
  ownerContact,
  onOpenAgenda,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [addToAgenda, setAddToAgenda] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  /**
   * ¿Las coordenadas que van a guardarse las resolvió Google para la dirección
   * que se está viendo? El texto y la coordenada viajan por caminos separados:
   * los campos son inputs libres y `locationData` sólo cambia en `place_changed`.
   * Sin esta marca se guarda el texto de una ciudad con la coordenada de otra
   * —la del default del store, con la que nace el presupuesto— y el motor de
   * envíos cotiza la zona sólo por la coordenada (#328).
   */
  const [geoResolved, setGeoResolved] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const hasSavedAddress = Boolean(shippingAddress?.street1);

  const [locationData, setLocationData] = useState({
    lat: defaultCenter.lat.toString(),
    lng: defaultCenter.lng.toString(),
  });

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
    libraries,
    language: "es",
    region: "AR",
    version: "weekly",
  });

  // Pre-fill form when drawer opens
  useEffect(() => {
    if (!open) return;
    setIsEditing(true);
    setAddToAgenda(false);
    setHasChanges(false);
    const addr = shippingAddress;
    const lat = addr?.lat || currentAddress.latitude || defaultCenter.lat.toString();
    const lng = addr?.lng || currentAddress.longitude || defaultCenter.lng.toString();

    setLocationData({ lat, lng });
    // La dirección ya guardada se abre como resuelta: editar el teléfono no
    // puede obligar a volver a elegir la calle. La vacía arranca sin resolver.
    setGeoResolved(Boolean(addr?.street1 && addr?.lat && addr?.lng));

    const savedName = addr?.firstname
      ? `${addr.firstname || ""} ${addr.lastname || ""}`.trim()
      : "";
    form.setFieldsValue({
      name: savedName || ownerContact?.name || "",
      address: addr?.street1 || "",
      phone: addr?.phone || ownerContact?.phone || "",
      city: addr?.cityName || currentAddress.city || "",
      recipientId: addr?.vatId || ownerContact?.vatId || "",
      floor: addr?.street2 || "",
      instructions: addr?.reference || "",
      state: addr?.provinceName || currentAddress.region || "",
      zipCode: addr?.postcode || "",
    });
  }, [open, shippingAddress, currentAddress, form, ownerContact]);

  // Google Places Autocomplete handlers
  const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    if (!window.google) return;

    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(-55.174989, -73.563892),
      new window.google.maps.LatLng(-21.770833, -53.984375),
    );
    autocomplete.setBounds(bounds);
    autocomplete.setComponentRestrictions({ country: ["AR"] });

    autocompleteRef.current = autocomplete;

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place?.address_components && place.geometry?.location) {
        handlePlaceSelect(place);
      }
    });
  };

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (!place.geometry?.location) return;

    const lat = place.geometry.location.lat().toString();
    const lng = place.geometry.location.lng().toString();

    const components = place.address_components || [];
    const getComponent = (type: string) =>
      components.find((c) => c.types.includes(type))?.long_name;

    const fullAddress = [getComponent("route"), getComponent("street_number")]
      .filter(Boolean)
      .join(" ");

    form.setFieldsValue({
      address: fullAddress || place.formatted_address || "",
      city: getComponent("locality") || getComponent("administrative_area_level_2") || "",
      state: getComponent("administrative_area_level_1") || "",
      zipCode: getComponent("postal_code") || "",
    });

    setLocationData({ lat, lng });
    setGeoResolved(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (!geoResolved) {
        form.setFields([
          {
            name: "address",
            errors: ["Elegí tu dirección de la lista que aparece al escribir"],
          },
        ]);
        message.error("Elegí tu dirección de la lista que aparece al escribir");
        return;
      }

      setSaving(true);

      const nameParts = (values.name || "").trim().split(/\s+/);
      const firstname = nameParts[0] || "";
      const lastname = nameParts.slice(1).join(" ") || "";

      const payload = {
        type: "shipping",
        firstname,
        lastname,
        phone: values.phone || "",
        street1: values.address || "",
        street2: values.floor || "",
        cityName: values.city || "",
        provinceName: values.state || "",
        postcode: values.zipCode || "",
        reference: values.instructions || "",
        vatId: values.recipientId || "",
        lat: locationData.lat || null,
        lng: locationData.lng || null,
      };

      await axiosInstance.patch(`${API_URL_QUOTES}/${quoteId}/addresses`, payload);

      if (addToAgenda) {
        if (!userId) {
          message.warning(
            "Dirección guardada en el presupuesto. No hay owner con usuario asignado: no se pudo agregar a la agenda.",
          );
        } else {
          // The agenda lives in `xconsadmindb.user_addresses` (one row per
          // type). Reuse the same payload shape as the snapshot since both
          // tables share the v1 schema.
          const agendaPayload: Record<string, any> = {
            type: "shipping",
            userId,
            store: storeCode || null,
            firstname,
            lastname,
            phone: values.phone || "",
            street1: values.address || "",
            street2: values.floor || null,
            cityName: values.city || "",
            provinceName: values.state || "",
            postcode: values.zipCode || "",
            countryId: "AR",
            reference: values.instructions || "",
            vatId: values.recipientId || "",
            lat: locationData.lat || null,
            lng: locationData.lng || null,
          };
          try {
            await axiosInstance.post(API_URL_USER_ADDRESSES_V1, agendaPayload);
          } catch (e: any) {
            console.error("[agenda shipping] POST failed", {
              payload: agendaPayload,
              response: e?.response?.data,
              status: e?.response?.status,
            });
            const apiMsg =
              e?.response?.data?.message || e?.response?.data?.error;
            const detail = Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg;
            message.warning(
              detail
                ? `Dirección guardada en el presupuesto, pero no se pudo agregar a la agenda: ${detail}`
                : "Dirección guardada en el presupuesto, pero no se pudo agregar a la agenda.",
            );
          }
        }
      }

      message.success("Dirección actualizada");
      onSave();
    } catch (e: any) {
      // Un campo inválido ya se marca en el form: el cartel queda para cuando falla el
      // guardado de verdad. Antes cualquier "Requerido" mostraba "Error al guardar".
      if (e?.errorFields) return;
      message.error("Error al guardar dirección");
    } finally {
      setSaving(false);
    }
  };

  const mapCenter = {
    lat: parseFloat(locationData.lat) || defaultCenter.lat,
    lng: parseFloat(locationData.lng) || defaultCenter.lng,
  };

  return (
    <Drawer
      title={
        <Flex align="center" gap={8}>
          <span>Datos de retiro o entrega</span>
          {userId && onOpenAgenda && (
            <span
              onClick={() => onOpenAgenda()}
              style={{ cursor: "pointer", display: "inline-flex", lineHeight: 0 }}
              title="Buscar en la agenda del dueño"
            >
              <svg width="24" height="26" viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_d_12345_53592)">
                  <mask id="path-1-inside-1_12345_53592" fill="white">
                    <path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z"/>
                  </mask>
                  <path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z" fill="white" shapeRendering="crispEdges"/>
                  <path d="M4 0V1H20V0V-1H4V0ZM24 4H23V20H24H25V4H24ZM20 24V23H4V24V25H20V24ZM0 20H1V4H0H-1V20H0ZM4 24V23C2.34315 23 1 21.6569 1 20H0H-1C-1 22.7614 1.23858 25 4 25V24ZM24 20H23C23 21.6569 21.6569 23 20 23V24V25C22.7614 25 25 22.7614 25 20H24ZM20 0V1C21.6569 1 23 2.34315 23 4H24H25C25 1.23858 22.7614 -1 20 -1V0ZM4 0V-1C1.23858 -1 -1 1.23858 -1 4H0H1C1 2.34315 2.34315 1 4 1V0Z" fill="#D9D9D9" mask="url(#path-1-inside-1_12345_53592)"/>
                  <path d="M15.1178 15.7647L15.6069 8.85822C15.6114 8.79575 15.6024 8.73306 15.5805 8.67439C15.5586 8.61572 15.5243 8.56243 15.4801 8.51815C15.4358 8.47387 15.3825 8.43963 15.3238 8.41774C15.2652 8.39585 15.2025 8.38682 15.14 8.39128L8.23452 8.88142C8.21369 8.88287 8.1937 8.89029 8.17696 8.90278C8.16021 8.91526 8.14741 8.9323 8.14008 8.95186C8.13274 8.97142 8.13118 8.99268 8.13559 9.0131C8.13999 9.03352 8.15017 9.05224 8.16492 9.06704L8.94798 9.8501C8.99245 9.89457 9.05433 9.91777 9.11813 9.91391L13.6734 9.59101L8.22969 15.0348C8.18715 15.0773 8.18715 15.1469 8.22969 15.1894L8.80974 15.7695C8.85228 15.812 8.92188 15.812 8.96442 15.7695L14.4082 10.3257L14.0853 14.8811C14.0804 14.9439 14.1036 15.0058 14.1491 15.0512L14.9322 15.8343C14.9979 15.9 15.111 15.8584 15.1178 15.7647Z" fill="black" fillOpacity="0.88"/>
                </g>
                <defs>
                  <filter id="filter0_d_12345_53592" x="0" y="0" width="24" height="26" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="2"/>
                    <feComposite in2="hardAlpha" operator="out"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.02 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_12345_53592"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_12345_53592" result="shape"/>
                  </filter>
                </defs>
              </svg>
            </span>
          )}
        </Flex>
      }
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        isEditing ? (
          <Flex justify="space-between" align="center">
            {(() => {
              const agendaDisabled = !userId || (hasSavedAddress && !hasChanges);
              const agendaWarning = !userId
                ? "El presupuesto necesita dueño para poder agregar la dirección a una agenda"
                : "La dirección ya esta guardada en la agenda";
              return (
                <Flex
                  align="center"
                  gap={8}
                  style={{ cursor: agendaDisabled ? "not-allowed" : "default" }}
                  onClick={() => {
                    if (agendaDisabled) message.warning(agendaWarning);
                  }}
                >
                  <Text style={{ fontSize: 13, opacity: agendaDisabled ? 0.4 : 1 }}>Agregar a la agenda</Text>
                  <Switch
                    checked={addToAgenda}
                    onChange={setAddToAgenda}
                    size="small"
                    disabled={agendaDisabled}
                  />
                </Flex>
              );
            })()}
            <Flex gap={8}>
              <Button
                onClick={hasSavedAddress ? () => setIsEditing(false) : onClose}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                loading={saving}
                onClick={handleSave}
                icon={<CheckOutlined />} iconPosition="end"
              >
                Guardar
              </Button>
            </Flex>
          </Flex>
        ) : null
      }
    >
      {!isLoaded ? (
        <Flex justify="center" style={{ padding: 40 }}>
          <Spin />
        </Flex>
      ) : !isEditing ? (
        <Flex vertical gap={12}>
          <Flex vertical gap={4}>
            <Text strong>
              {`${shippingAddress?.firstname || ""} ${shippingAddress?.lastname || ""}`.trim() || "—"}
            </Text>
            {shippingAddress?.phone && <Text>{shippingAddress.phone}</Text>}
            <Text>{shippingAddress?.street1 || "—"}</Text>
            {shippingAddress?.street2 && (
              <Text type="secondary">{shippingAddress.street2}</Text>
            )}
            <Text type="secondary">
              {[
                shippingAddress?.cityName,
                shippingAddress?.provinceName,
                shippingAddress?.postcode
                  ? `(${shippingAddress.postcode})`
                  : null,
              ]
                .filter(Boolean)
                .join(", ")}
            </Text>
            {shippingAddress?.reference && (
              <Text style={{ marginTop: 4 }}>
                {shippingAddress.reference}
              </Text>
            )}
          </Flex>

          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={15}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              gestureHandling: "none",
              clickableIcons: false,
              styles: [
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ],
            }}
          >
            <MarkerF
              position={mapCenter}
              icon={
                window.google
                  ? {
                      url: xconsMap,
                      scaledSize: new window.google.maps.Size(45, 45),
                    }
                  : undefined
              }
            />
          </GoogleMap>

          <Flex justify="end">
            <Button
              shape="round"
              icon={<EditOutlined />}
              iconPosition="end"
              onClick={() => setIsEditing(true)}
            >
              Editar
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed) => {
            setHasChanges(true);
            // Sólo la calle invalida la marca: corregir la ciudad, el CP o el
            // teléfono no mueve la coordenada de lugar. `setFieldsValue` (lo que
            // usa handlePlaceSelect) no pasa por acá.
            if ("address" in changed) setGeoResolved(false);
          }}
        >
          <Flex gap={16}>
            <Form.Item
              label="Nombre y apellido"
              name="name"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Nombre y apellido" />
            </Form.Item>
            <Form.Item
              label="Dirección (calle y número)"
              name="address"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
              extra="Elegí una opción del listado para ubicar la entrega"
            >
              <AddressAutocompleteInput
                onLoad={onAutocompleteLoad}
                placeholder="Ingresá la dirección completa como en Google Maps"
              />
            </Form.Item>
          </Flex>

          <Flex gap={16}>
            <Form.Item
              label="Teléfono"
              name="phone"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: "Requerido" },
                {
                  // Un número incompleto se guardaba igual y salía así al pedido y al ERP.
                  validator: (_, value) =>
                    !value || isValidArPhone(value)
                      ? Promise.resolve()
                      : Promise.reject(AR_PHONE_INVALID_MESSAGE),
                },
              ]}
            >
              <PhoneNumberInput />
            </Form.Item>
            <Form.Item
              label="Ciudad"
              name="city"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Ingresá la ciudad" />
            </Form.Item>
          </Flex>

          <Flex gap={16}>
            <Form.Item
              label="DNI del destinatario"
              name="recipientId"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: "Requerido" },
                {
                  // Un DNI de 9 dígitos se guardaba y el pedido se trababa con
                  // "completá la dirección", sin decir cuál campo (#378).
                  validator: (_, value) =>
                    !value || isValidDni(value)
                      ? Promise.resolve()
                      : Promise.reject("El DNI tiene que tener 7 u 8 dígitos"),
                },
              ]}
            >
              <Input placeholder="Ingresá el DNI" />
            </Form.Item>
            <Form.Item
              label="Piso/dpto. o manzana y lote"
              name="floor"
              style={{ flex: 1 }}
            >
              <Input placeholder="Ingresá piso/dpto. o manzana..." />
            </Form.Item>
          </Flex>

          <Flex gap={16} align="stretch">
            <Form.Item
              label="Indicaciones de entrega"
              name="instructions"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input.TextArea
                style={{ height: 120, resize: "none" }}
                autoSize={false}
                rows={6}
                placeholder="Indicaciones para la entrega. Ej: la puerta es verde"
              />
            </Form.Item>
            <Flex vertical style={{ flex: 1 }}>
              <Form.Item
                label="Provincia"
                name="state"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Select
                  placeholder="Seleccioná la provincia"
                  showSearch
                  options={ARGENTINA_PROVINCES.map((p) => ({
                    value: p,
                    label: p,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Código postal"
                name="zipCode"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Ingresá el código postal" />
              </Form.Item>
            </Flex>
          </Flex>

          <Flex justify="flex-end" style={{ marginBottom: 12 }}>
            <Flex align="center" gap={4}>
              <EnvironmentOutlined style={{ color: "#15744b" }} />
              <Text style={{ color: "#15744b", cursor: "pointer", fontSize: 13 }}>
                Ubicar mi posición
              </Text>
            </Flex>
          </Flex>

          {/* Interactive map */}
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={15}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              styles: [
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ],
            }}
          >
            <MarkerF
              position={mapCenter}
              icon={
                window.google
                  ? {
                      url: xconsMap,
                      scaledSize: new window.google.maps.Size(45, 45),
                    }
                  : undefined
              }
            />
          </GoogleMap>
        </Form>
      )}
    </Drawer>
  );
};
